import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { type ProposalDeadlineRadarItem, summarizeDeadlineRadar, type TripDto } from "@tc/shared";
import { Button, Card, CardHead, CardTitle, Icon, type IconName } from "@/components/ui";
import { calendarApi } from "@/features/calendar/api";
import { tripsApi } from "@/features/trips/api";
import { dateTone, TONE_BG, TONE_COLOR } from "@/features/trips/status";
import { TripCard } from "@/features/trips/TripCard";
import { Can, useAuthz } from "@/lib/auth/authz";
import { useFormatters } from "@/lib/i18n/format";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; trips: TripDto[] }
  | { kind: "error" };

/**
 * The club's landing page (TW-3439): KPIs, an upcoming-travel feed and quick actions, all derived
 * from the club's own RLS-scoped trips (`GET /api/trips`) — there is no separate metrics endpoint,
 * and proposal/deposit/flight deadlines arrive with Module 3, so every number here is a fold over
 * the trip list. Back-office users land here too (the shell picks the audience); the surface stays
 * club-flavoured but never assumes a role by name — actions are permission-gated.
 */
export function Dashboard() {
  const { t } = useTranslation();
  const { me } = useAuthz();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    tripsApi
      .list()
      .then((trips) => {
        if (!cancelled) setState({ kind: "ready", trips });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const firstName = (me?.name ?? "").split(" ")[0] ?? "";

  return (
    <div className="page">
      <div className="row-between page-head">
        <div>
          <h1>
            {t("dashboard.hello")}
            {firstName && `, ${firstName}`}
          </h1>
          <div className="sub">{t("dashboard.sub")}</div>
        </div>
        <Can permission="trip.write">
          <NewRequestButton />
        </Can>
      </div>

      {state.kind === "loading" && <div className="empty">{t("common.loading")}</div>}
      {state.kind === "error" && <div className="empty">{t("dashboard.loadError")}</div>}
      {state.kind === "ready" && <DashboardBody trips={state.trips} />}
    </div>
  );
}

function NewRequestButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Button variant="primary" icon="plus" onClick={() => navigate("/request")}>
      {t("dashboard.newTrip")}
    </Button>
  );
}

function DashboardBody({ trips }: { trips: TripDto[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cards = trips
    .filter((tr) => tr.status !== "Completed" && tr.status !== "Cancelled")
    .sort((a, b) => (a.travelStartDate ?? "9999-12-31").localeCompare(b.travelStartDate ?? "9999-12-31"))
    .slice(0, 4);

  return (
    <>
      <div className="dash-grid">
        <div className="stack">
          <div className="row-between" style={{ alignItems: "center" }}>
            <h2 style={{ fontSize: 17, whiteSpace: "nowrap" }}>{t("dashboard.recentTrips")}</h2>
            <Button variant="ghost" size="sm" iconRight="arrowR" onClick={() => navigate("/trips")}>
              {t("dashboard.viewAll")}
            </Button>
          </div>
          {cards.length === 0 ? (
            <Card>
              <div className="empty">
                <div className="empty-ic">
                  <Icon name="trips" size={24} />
                </div>
                {t("dashboard.noTrips")}
              </div>
            </Card>
          ) : (
            <div className="stack">
              {cards.map((tr) => (
                <TripCard key={tr.id} trip={tr} />
              ))}
            </div>
          )}
        </div>

        <div className="stack">
          <UpcomingDeadlines trips={trips} />
          <QuickActions />
        </div>
      </div>
    </>
  );
}

/**
 * The club's key deadlines, soonest (and overdue) first — what the club still needs to act on:
 * accept a proposal, accept a train-travel proposal, submit the passenger list, etc. Backed by the
 * shared `GET /api/proposals/calendar-deadlines` (RLS-scoped to the club's own live proposals — the
 * same pending-deadline feed the calendar plots and the back-office radar uses). The deadline feed
 * carries only the trip reference, so the destination is joined from the club's already-loaded trips.
 */
function UpcomingDeadlines({ trips }: { trips: TripDto[] }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();
  const [items, setItems] = useState<ProposalDeadlineRadarItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    calendarApi
      .deadlines()
      .then((d) => {
        if (!cancelled) setItems(d);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const destById = new Map(
    trips.map((tr) => [
      tr.id,
      [tr.destinationCity, tr.destinationCountry ? fmt.country(tr.destinationCountry) : ""].filter(Boolean).join(", "),
    ]),
  );

  const now = new Date().toISOString();
  const rows = items ? summarizeDeadlineRadar(items, now).items.slice(0, 5) : [];

  return (
    <Card>
      <CardHead>
        <div>
          <CardTitle>{t("dashboard.deadlinesTitle")}</CardTitle>
          <div className="faint" style={{ fontSize: 12.5, marginTop: 2 }}>
            {t("dashboard.deadlinesSub")}
          </div>
        </div>
        <Icon name="clock" size={18} className="faint" />
      </CardHead>
      {items == null ? (
        <div className="empty" style={{ padding: "24px 0" }}>
          {t("common.loading")}
        </div>
      ) : rows.length === 0 ? (
        <div className="empty" style={{ padding: "24px 0" }}>
          {t("dashboard.noDeadlines")}
        </div>
      ) : (
        rows.map((item) => {
          const tone = dateTone(item.date);
          const dest = destById.get(item.tripId) ?? "";
          return (
            <div
              className="deadline-row"
              key={`${item.proposalId}:${item.deadlineKey}`}
              onClick={() => navigate(`/trips/${item.tripId}`)}
              style={{ cursor: "pointer" }}
            >
              <span className="dl-ic" style={{ background: TONE_BG[tone], color: TONE_COLOR[tone] }}>
                <Icon name="clock" size={17} />
              </span>
              <div className="dl-body">
                <div className="dl-title">{item.label ?? (item.labelKey ? t(item.labelKey) : "")}</div>
                <div className="dl-meta">
                  {item.tripReference ?? "—"}
                  {dest && ` · ${dest}`}
                </div>
              </div>
              <div className="dl-when" style={{ color: TONE_COLOR[tone] }}>
                {tone === "overdue" ? t("proposal.dlOverdue") : fmt.relative(item.date)}
                <small>{fmt.date(item.date)}</small>
              </div>
            </div>
          );
        })
      )}
    </Card>
  );
}

function QuickActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items: { ic: IconName; title: string; desc: string; to: string }[] = [
    { ic: "plus", title: t("dashboard.qaRequest"), desc: t("dashboard.qaRequestDesc"), to: "/request" },
    { ic: "roster", title: t("dashboard.qaRoster"), desc: t("dashboard.qaRosterDesc"), to: "/roster" },
    { ic: "messages", title: t("dashboard.qaMessages"), desc: t("dashboard.qaMessagesDesc"), to: "/messages" },
  ];
  return (
    <Card>
      <CardTitle style={{ marginBottom: 14 }}>{t("dashboard.quickTitle")}</CardTitle>
      <div className="stack" style={{ gap: 10 }}>
        {items.map((q) => (
          // Intentional repurpose: this reuses the `.check-card` visual shell as a nav tile, NOT the
          // kit CheckCard (whose contract is a controlled checkbox) — keep the raw button markup.
          <button key={q.to} className="check-card" onClick={() => navigate(q.to)} style={{ cursor: "pointer" }}>
            <span className="kpi-ic" style={{ width: 36, height: 36 }}>
              <Icon name={q.ic} size={17} />
            </span>
            <span className="check-card-text">
              <span className="check-card-label">{q.title}</span>
              <span className="check-card-sub">{q.desc}</span>
            </span>
            <Icon name="chevR" size={16} className="faint" style={{ marginLeft: "auto" }} />
          </button>
        ))}
      </div>
    </Card>
  );
}
